import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { Grid } from '@mantine/core'
import useStyles from './SpotifyInCoffeeShops.styles'
import { translate } from '../../../../utilities/language'
import { getAttribution } from '../../../../utilities/content'
import {
  ARTICLE_IMAGE_AVIF,
  ARTICLE_IMAGE_JPEG,
  ARTICLE_IMAGE_JPEG_ES_FR_KO,
  ARTICLE_IMAGE_AVIF_ES_FR_KO,
  SPOTIFY_IN_COFFEE_SHOPS_TITLE_HTML,
  SPOTIFY_IN_COFFEE_SHOPS_TITLE_HTML_ES,
  SPOTIFY_IN_COFFEE_SHOPS_TITLE_HTML_FR,
  SPOTIFY_IN_COFFEE_SHOPS_TITLE_HTML_KO,
  SPOTIFY_IN_COFFEE_SHOPS_ARTICLE_PUBLISHED_STR,
} from '../../../../static-data/spotify-in-coffee-shops-data'
import {
  MAIN_SITE_ENVIRONMENT_HOST,
  MAIN_SITE_ENVIRONMENT_HOST_ES,
  MAIN_SITE_ENVIRONMENT_HOST_FR,
  MAIN_SITE_ENVIRONMENT_HOST_KO,
} from '../../../../utilities/config'

const BackToArticle: React.ComponentType<any> = dynamic(
  () => import('../../../controls/BackToArticle'),
  {
    ssr: true,
  },
)
const ReadyToTransform: React.ComponentType<any> = dynamic(
  () => import('../../../controls/ReadyToTransform'),
  {
    ssr: true,
  },
)

export default function SpotifyInCoffeeShops(props: any) {
  const { classes } = useStyles()
  const selectedLanguage: any = props?.selectedLanguage
  const avifCompatible = props?.avifCompatible

  const articleImage = useMemo(() => {
    return (
      <Image
        width={512}
        height={279}
        sizes="(max-width: 350px) 256px, 
         (max-width: 430px) 300px, 
         (max-width: 590px) 384px, 
         512px"
        src={
          avifCompatible
            ? translate(
                ARTICLE_IMAGE_AVIF,
                ARTICLE_IMAGE_AVIF_ES_FR_KO,
                ARTICLE_IMAGE_AVIF_ES_FR_KO,
                ARTICLE_IMAGE_AVIF_ES_FR_KO,
                selectedLanguage,
              )
            : translate(
                ARTICLE_IMAGE_JPEG,
                ARTICLE_IMAGE_JPEG_ES_FR_KO,
                ARTICLE_IMAGE_JPEG_ES_FR_KO,
                ARTICLE_IMAGE_JPEG_ES_FR_KO,
                selectedLanguage,
              )
        }
        alt=""
        priority={true}
        style={{
          marginTop: '30px',
          borderRadius: '10px',
          display: 'block',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
        }}
      />
    )
  }, [selectedLanguage, avifCompatible])

  return (
    <div className={classes.mainGrid}>
      <div className={classes.innerDiv}>
        <Grid columns={24}>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <h1 className={classes.standardText + ' ' + classes.h1Tag}>
                {translate(
                  SPOTIFY_IN_COFFEE_SHOPS_TITLE_HTML,
                  SPOTIFY_IN_COFFEE_SHOPS_TITLE_HTML_ES,
                  SPOTIFY_IN_COFFEE_SHOPS_TITLE_HTML_FR,
                  SPOTIFY_IN_COFFEE_SHOPS_TITLE_HTML_KO,
                  selectedLanguage,
                )}
              </h1>
              {getAttribution(
                translate(
                  'Editor',
                  'Editora',
                  'Éditrice',
                  '편집자',
                  selectedLanguage,
                ),
                SPOTIFY_IN_COFFEE_SHOPS_ARTICLE_PUBLISHED_STR,
                selectedLanguage,
                false,
              )}
              {articleImage}
            </div>
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  <span className={classes.spanItalic}>
                    Most café owners have no idea they’re breaking copyright law
                    every single day. Here’s the fix.
                  </span>
                </>,
                <>
                  <span className={classes.spanItalic}>
                    La mayoría de los dueños de cafeterías no tienen ni idea de
                    que están infringiendo la ley de derechos de autor todos y
                    cada uno de los días. Aquí está la solución.
                  </span>
                </>,
                <>
                  <span className={classes.spanItalic}>
                    La plupart des propriétaires de cafés ignorent qu'ils
                    enfreignent le droit d'auteur chaque jour. Voici la
                    solution.
                  </span>
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    대부분의
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    카페
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    사장님들은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    자신들이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    매일같이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    저작권법을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    위반하고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있다는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    사실조차
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    모르고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있습니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    여기,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    해결책이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있습니다.
                  </span>
                </>,
                selectedLanguage,
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>She had saved for three years to open it.</>,
                <>Ahorró dinero durante tres años para abrirlo.</>,
                <>
                  Elle a économisé de l'argent pendant trois ans pour l'ouvrir.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그녀는 분위기에
                  </span>{' '}
                  그것을
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  열기
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  위해
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  3년
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  동안
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  돈을
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  모았다.
                </>,
                selectedLanguage,
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  The corner spot with the exposed brick, the single-origin
                  pour-overs, the playlist she’d spent weeks curating.
                  Everything was perfect. Then, eight months in, a letter
                  arrived from a performing rights organization. They’d had
                  someone visit the café. They’d noted the music playing. And
                  now they were requesting a settlement.
                </>,
                <>
                  El local de la esquina con sus paredes de ladrillo visto, los
                  cafés de origen único preparados al momento, la lista de
                  reproducción que ella había pasado semanas seleccionando. Todo
                  era perfecto. Entonces, a los ocho meses, llegó una carta de
                  una organización de derechos de ejecución pública. Habían
                  enviado a alguien a visitar la cafetería. Habían tomado nota
                  de la música que sonaba. Y ahora exigían un acuerdo económico.
                </>,
                <>
                  Ce petit coin de rue aux murs de briques apparentes, ces cafés
                  filtres d’origine unique, cette playlist qu’elle avait passé
                  des semaines à concocter… Tout était parfait. Puis, au bout de
                  huit mois, une lettre arriva, émanant d’une société de gestion
                  des droits d’auteur. Ils avaient envoyé quelqu’un visiter le
                  café. Ils avaient relevé la musique qui y était diffusée. Et,
                  désormais, ils réclamaient un règlement.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    노출된
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    벽돌
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    벽과
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    세련된
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    코너
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    좌석,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    싱글
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    오리진
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    핸드
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    드립
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    커피,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    심지어
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    몇
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    주에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    걸쳐
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그녀가
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    공들여
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    선곡한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    플레이리스트에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이르기까지—모든
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    것이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    완벽했다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    하지만
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    영업을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    시작한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    지
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    8개월째
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    되던
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    어느
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    날,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    저작권
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    관리
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    단체로부터
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    통의
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    편지가
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    도착했다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    알고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    보니
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그들은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    카페에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    조사관을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    파견해
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실사를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    진행했던
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    것이었고,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    배경
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악으로
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    흘러나오던
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    곡들을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    확인한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    뒤
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이제
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    와서
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    합의금을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    요구하고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있었다.
                  </span>
                </>,
                selectedLanguage,
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Four thousand dollars&nbsp;— or they’d pursue full statutory
                  damages.
                </>,
                <>
                  $4,000; de lo contrario, reclamaré el monto total de los daños
                  y perjuicios legales.
                </>,
                <>
                  4&nbsp;000&nbsp;$&nbsp;— faute de quoi, je réclamerai le
                  montant intégral des dommages-intérêts légaux.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    4,000달러—그렇지 분위기에
                  </span>{' '}
                  않으면
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  법정
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  손해배상액
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  전액을
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기에
                  </span>{' '}
                  청구하겠다.
                </>,
                selectedLanguage,
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>Up to $150,000. Per song.</>,
                <>Hasta $150.000. Por canción.</>,
                <>Jusqu'à 150&nbsp;000&nbsp;$. Par chanson.</>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    최대
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    15만
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    달러.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    곡당.
                  </span>
                </>,
                selectedLanguage,
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  She’d been playing her personal Spotify account through a
                  Bluetooth speaker. Eleven dollars a month. She had no idea
                  that was illegal.
                </>,
                <>
                  Había estado reproduciendo su cuenta personal de Spotify a
                  través de un altavoz Bluetooth. Once dólares al mes. No tenía
                  ni idea de que eso fuera ilegal.
                </>,
                <>
                  Elle diffusait son compte Spotify personnel sur une enceinte
                  Bluetooth. Onze dollars par mois. Elle n’avait aucune idée que
                  c’était illégal.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그녀는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    블루투스
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    스피커를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    통해
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    자신의
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    개인
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    스포티파이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    계정을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    틀어놓고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있었다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    달에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    11달러짜리였다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그녀는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그게
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    불법이라는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    사실을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    전혀
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    몰랐다.
                  </span>
                </>,
                selectedLanguage,
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  This story isn’t rare. It’s playing out in coffee shops,
                  salons, gyms, and restaurants across the country right now.
                  And the frustrating part? The fix is simple, affordable, and
                  most owners just don’t know it exists.
                </>,
                <>
                  Esta historia no tiene nada de inusual. Se está desarrollando
                  justo en este preciso momento en cafeterías, salones de
                  belleza, gimnasios y restaurantes... en todo el país. ¿Y lo
                  más frustrante? La solución es sencilla, asequible, y la
                  mayoría de los propietarios simplemente no saben que existe.
                </>,
                <>
                  Cette histoire n’a rien de rare. Elle se joue en ce moment
                  même dans les cafés, les salons de coiffure, les salles de
                  sport et les restaurants, partout dans le pays. Et le plus
                  frustrant&nbsp;? La solution est simple, abordable, et la
                  plupart des propriétaires ignorent tout simplement qu’elle
                  existe.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이야기는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    결코
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    특별한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    일이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    아닙니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    바로
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    지금
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    순간에도
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    전국의
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    카페,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    미용실,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    체육관,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    식당
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    등
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    곳곳에서
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    벌어지고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    일입니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그런데
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    가장
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    답답한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    점이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    무엇일까요?
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    해결책은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    간단하고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    비용
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    부담도
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    적은데,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    정작
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    대부분의
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    업주들은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그러한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    해결책이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    존재한다는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    사실조차
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    모르고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있다는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    것입니다.
                  </span>
                </>,
                selectedLanguage,
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Let me walk you through everything&nbsp;— the legal reality,
                  the right music for every hour of your day, and how to set
                  this up properly without spending more than the price of a few
                  bags of good beans each month.
                </>,
                <>
                  Te guiaré a través de cada detalle: desde los aspectos legales
                  hasta cómo seleccionar la música perfecta para cada momento
                  del día, e incluso cómo configurarlo todo adecuadamente, por
                  un costo mensual equivalente a tan solo unas pocas bolsas de
                  granos de café *premium*.
                </>,
                <>
                  Je vous guiderai à travers chaque détail&nbsp;— des réalités
                  juridiques au choix de la musique idéale pour chaque moment de
                  la journée, et même la manière de tout configurer
                  correctement, pour un coût mensuel équivalent à seulement
                  quelques sacs de grains de café haut de gamme.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    제가
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    모든
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    것을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    자세히
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    안내해
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    드리겠습니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    법적인
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    현실부터,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    하루
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    중
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    매
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    순간에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    딱
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    맞는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    선정법,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그리고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    매달
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    고급
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    원두
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    몇
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    봉지
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    값
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    정도의
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    비용만으로
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    모든
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    것을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    제대로
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    구축하는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    방법까지
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    말이죠.
                  </span>
                </>,
                selectedLanguage,
              )}
            </p>
            <section id={'what-to-learn-in-this-guide'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    First, the Uncomfortable Truth About That Spotify Premium
                    Account
                  </>,
                  <>
                    Primero, la verdad incómoda sobre esa cuenta de Spotify
                    Premium.
                  </>,
                  <>
                    Tout d'abord, la vérité qui dérange à propos de ce compte
                    Spotify Premium.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Premium
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      계정에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      불편한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      진실
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Here’s what the terms of service actually say: personal
                    streaming licenses&nbsp;— Spotify, Apple Music, YouTube
                    Music, all of them&nbsp;— are for personal, non-commercial
                    use only.
                  </>,
                  <>
                    Lo que se establece realmente en los Términos de servicio es
                    lo siguiente: las Licencias de Streaming Personal&nbsp;—las
                    licencias para todos dichos servicios, incluidos Spotify,
                    Apple Music y YouTube Music— podrán utilizarse únicamente
                    con fines personales y no comerciales.
                  </>,
                  <>
                    Ce qui est effectivement stipulé dans les Conditions
                    d'utilisation est le suivant&nbsp;: les licences de
                    streaming personnel&nbsp;— c'est-à-dire les licences pour
                    l'ensemble de ces services, y compris Spotify, Apple Music
                    et YouTube Music&nbsp;— ne peuvent être utilisées qu'à des
                    fins personnelles et non commerciales.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서비스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      약관에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      실제로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      명시된
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      내용은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다음과
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      같습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      개인용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      스트리밍
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스—Spotify,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Apple
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Music,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      YouTube
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Music
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      등
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      오직
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      개인적이고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비상업적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      용도로만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사용할
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The moment you play that music in your café, you’ve crossed
                    into commercial use. Your $11/month subscription gives you
                    zero rights to do that.
                  </>,
                  <>
                    En el momento en que reproduces esta música en tu cafetería,
                    quedas sujeto al ámbito del uso comercial. Tu suscripción de
                    11 dólares al mes no te otorga absolutamente ningún derecho
                    para hacerlo.
                  </>,
                  <>
                    Dès l'instant où vous diffusez cette musique dans votre
                    café, vous basculez dans le cadre d'une utilisation
                    commerciale. Votre abonnement à 11&nbsp;$ par mois ne vous
                    confère absolument aucun droit de le faire.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      재생하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      순간,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      귀하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      상업적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이용의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      범위에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      해당하게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      됩니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      월
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      11달러의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독료로는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그러한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      권리가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      전혀
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      부여되지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      않습니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The law requires businesses to hold what are called
                    performance rights&nbsp;— licenses from organizations like
                    ASCAP, BMI, and SESAC that compensate artists and
                    songwriters when their work is performed publicly.
                    “Publicly” includes your café. Even with ten tables. Even if
                    it’s just background noise.
                  </>,
                  <>
                    La ley exige a los negocios poseer lo que se denomina
                    «derechos de ejecución pública»&nbsp;—licencias otorgadas
                    por organizaciones como ASCAP, BMI y SESAC, las cuales
                    compensan a los artistas y compositores cuando sus obras se
                    interpretan en público—. El término «en público» incluye su
                    cafetería. Incluso si solo tiene diez mesas. Incluso si se
                    trata meramente de música de fondo.
                  </>,
                  <>
                    La loi exige que les entreprises détiennent ce que l'on
                    appelle des «&nbsp;droits d'exécution&nbsp;»&nbsp;— des
                    licences délivrées par des organismes tels que l'ASCAP, la
                    BMI et la SESAC, qui rémunèrent les artistes et les
                    auteurs-compositeurs lorsque leurs œuvres sont exécutées en
                    public. Le terme «&nbsp;en public&nbsp;» inclut votre café.
                    Même s'il ne compte que dix tables. Même s'il ne s'agit que
                    d'un simple fond sonore.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      현행법에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      따라,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사업장은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소위
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      '공중
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      연주권(public
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      performance
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      right)'에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보유해야
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      합니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구체적으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      ASCAP,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      BMI,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      SESAC
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      등과
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      같은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단체에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      발급하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      의미하며,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      통해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아티스트와
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      작곡가는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      자신의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      저작물이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대중
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      앞에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      연주될
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      때마다
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보상을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지급받게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      됩니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여기서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      '대중
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      앞(in
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      public)'이라는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      용어의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      범위에는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      귀하의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      카페
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      또한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      포함됩니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      규정에는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어떠한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      예외도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      적용되지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      않습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      설령
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      귀하의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      매장에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      테이블이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      10개뿐이라
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하더라도,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      혹은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단순히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      배경
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악으로만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      재생한다
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하더라도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      예외는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      없습니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>Here’s what makes this genuinely alarming:</>,
                  <>
                    Esto es lo que hace que esto sea verdaderamente alarmante:
                  </>,
                  <>Voici ce qui rend cela véritablement alarmant&nbsp;:</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이것을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      진정으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      우려스럽게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만드는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      점은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이것입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Copyright infringement penalties run up to $150,000 per
                      song
                    </>,
                    <>
                      Las sanciones por infracción de derechos de autor
                      ascienden hasta a 150.000 dólares por canción.
                    </>,
                    <>
                      Les sanctions pour violation des droits d'auteur peuvent
                      atteindre 150&nbsp;000&nbsp;$ par chanson.
                    </>,
                    <>
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        저작권
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        침해에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        대한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        벌금은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        곡당
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        최대
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        15만
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        달러에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        분위기에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        달합니다.
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      PROs (performing rights organizations) actively send
                      people into businesses to check
                    </>,
                    <>
                      Las PRO (organizaciones de derechos de ejecución) envían
                      activamente personas a los negocios para realizar
                      inspecciones.
                    </>,
                    <>
                      Les PRO (organismes de droits d'exécution) envoient
                      activement des personnes dans les entreprises pour
                      effectuer des vérifications.
                    </>,
                    <>
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실연권
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        관리
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        단체(PRO)들은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        점검을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        위해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사업장에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        인력을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        적극적으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        파견합니다.
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Ignorance of the law is not a legal defense</>,
                    <>La ignorancia de la ley no es una defensa legal.</>,
                    <>
                      L'ignorance de la loi ne constitue pas un moyen de
                      défense.
                    </>,
                    <>
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        법률의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        부지는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        법적
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        변호
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사유가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        되지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        않는다.
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Settlements&nbsp;— even “friendly” ones&nbsp;— commonly
                      run $2,000–$10,000
                    </>,
                    <>
                      Los acuerdos&nbsp;— incluso los «&nbsp;amistosos&nbsp;»—
                      suelen oscilar entre los 2.000 y&nbsp;los 10.000 dólares.
                    </>,
                    <>
                      Les règlements&nbsp;— même «&nbsp;à l'amiable&nbsp;»
                      &nbsp;—&nbsp;se situent couramment entre 2&nbsp;000&nbsp;$
                      et 10&nbsp;000&nbsp;$.
                    </>,
                    <>
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        합의금은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        &nbsp;—
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        심지어
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        ‘원만하게’
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이루어지는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        경우라
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        하더라도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        &nbsp;—
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        대개
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        2,000달러에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        10,000달러
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수준입니다.
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    One lawsuit costs more than a decade of doing this
                    correctly. That math should make your stomach drop.
                  </>,
                  <>
                    Una sola demanda cuesta más que una década de hacer esto
                    correctamente. Esa cuenta debería revolverte el estómago.
                  </>,
                  <>
                    Un seul procès coûte plus d'une décennie de travail effectué
                    correctement. Ce calcul devrait vous nouer l'estomac.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소송
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      번에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      드는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비용은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      10년
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      넘게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      올바르게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      처리하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      데
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      드는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비용보다
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      큽니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      계산
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      결과를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      접하면
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아마
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      등골이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      오싹해질
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
            </section>
            <section id={'traditional-offices-zone-based-audio'}>
              <h3 className={classes.subSection}>
                {translate(
                  <>
                    The Music That Actually Makes People Stay (and Spend More)
                  </>,
                  <>
                    La música que realmente hace que la gente se quede (y gaste
                    más)
                  </>,
                  <>
                    La musique qui incite vraiment les gens à rester (et à
                    dépenser davantage)
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사람들을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      실제로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      머물게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      (그리고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      많이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지갑을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      열게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악)
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </h3>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Before we get to the legal solution, let’s talk about why
                    this matters beyond compliance&nbsp;— because the right
                    music isn’t just legal protection, it’s one of the most
                    underutilized business tools you have.
                  </>,
                  <>
                    Antes de abordar las soluciones legales, examinemos primero
                    por qué este asunto reviste una importancia tan crítica, la
                    cual trasciende con creces el mero ámbito del cumplimiento
                    normativo. Esto se debe a que el uso adecuado de la música
                    no solo actúa como un medio de protección legal, sino que
                    representa uno de los activos más subutilizados entre las
                    herramientas empresariales a su disposición.
                  </>,
                  <>
                    Avant d'aborder les solutions juridiques, examinons tout
                    d'abord pourquoi cette question revêt une importance si
                    critique&nbsp;— s'étendant bien au-delà du simple cadre de
                    la conformité réglementaire. En effet, l'utilisation
                    adéquate de la musique ne constitue pas seulement un moyen
                    de protection juridique ; elle représente l'un des atouts
                    les plus sous-exploités parmi les outils commerciaux dont
                    vous disposez.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      법적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      해결책을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      논하기에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      앞서,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이것이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단순한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      법규
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      준수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      차원을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      넘어
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      왜
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그토록
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      중요한지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      먼저
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      살펴보겠습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      올바른
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      활용은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      법적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보호
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수단에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그치는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아니라,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여러분이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보유하고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비즈니스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      도구
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      중
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가장
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제대로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      활용되지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      못하고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      자산
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      중
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하나이기
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      때문입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Research consistently shows that music tempo and genre
                    directly affect customer behavior:
                  </>,
                  <>
                    Las investigaciones demuestran de manera consistente que el
                    tempo y el género musical afectan directamente el
                    comportamiento de los clientes:
                  </>,
                  <>
                    Les recherches démontrent de manière constante que le tempo
                    et le genre musical influent directement sur le comportement
                    des clients :
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      따르면,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      템포와
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      장르는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고객의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      행동에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      직접적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      영향을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      미치는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일관되게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      나타납니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>72 BPM: Customers slow down, linger, order more</>,
                    <>
                      72 BPM: Los clientes bajan el ritmo, se detienen y piden
                      más.
                    </>,
                    <>
                      72 BPM&nbsp;: Les clients ralentissent, s'attardent et
                      commandent davantage.
                    </>,
                    <>
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        72
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        BPM:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객들은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        속도를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        늦추고,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        더
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오래
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        머물며,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        더
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        많이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        주문합니다.
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      120+ BPM: Faster turnover&nbsp;— useful for morning
                      rushes, damaging during slower periods
                    </>,
                    <>
                      Más de 120 PPM: Mayor rotación&nbsp;— útil durante las
                      horas punta de la mañana, perjudicial durante los periodos
                      de menor actividad.
                    </>,
                    <>
                      120+ BPM : Rotation plus rapide&nbsp;— utile lors des
                      coups de feu matinaux, préjudiciable durant les périodes
                      plus calmes
                    </>,
                    <>
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        120+
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        BPM:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        더
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        빠른
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        회전율
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        &nbsp;—
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오전
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        피크
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        시간대에는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        유용하지만,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        한산한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        시간대에는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오히려
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        해가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        될
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있음
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    One study found that appropriate background music increased
                    customer dwell time by 40% and per-ticket spending by 15%.
                    Your invisible staff member, working every shift, never
                    calling in sick.
                  </>,
                  <>
                    Según un estudio, se ha demostrado que la música de fondo
                    adecuada aumenta en un 40 % el tiempo que los clientes pasan
                    en una tienda y en un 15 % su gasto promedio por visita. Es
                    su «empleado invisible»: uno que trabaja incansablemente
                    durante cada turno sin tomarse jamás un solo día por
                    enfermedad.
                  </>,
                  <>
                    Selon une étude, il a été démontré qu'une musique d'ambiance
                    appropriée augmente de 40 % le temps que les clients passent
                    en magasin et de 15 % leurs dépenses moyennes par visite.
                    C'est votre «&nbsp;employé invisible&nbsp;»&nbsp;— celui qui
                    travaille sans relâche à chaque service, sans jamais prendre
                    un seul jour de congé maladie.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      연구에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      따르면,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      적절한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      배경
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고객의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      매장
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      체류
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시간을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      40%,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      건당
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지출액을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      15%
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      증가시키는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      나타났습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      매
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      근무조마다
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      쉬지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      않고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일하며,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      번도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      병가를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      내는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      법이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      없는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      당신의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      '보이지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      않는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      직원'입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    But “appropriate” is doing a lot of work in that sentence.
                    Here’s what that actually looks like across your day.
                  </>,
                  <>
                    Pero la palabra «apropiado» carga con mucho peso en esa
                    frase. Así es, en realidad, como se ve eso a lo largo de tu
                    día.
                  </>,
                  <>
                    Mais le mot «&nbsp;approprié&nbsp;» joue un rôle
                    considérable dans cette phrase. Voici à quoi cela ressemble
                    concrètement au fil de votre journée.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      하지만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      문장에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      ‘적절하다’는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      말은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      꽤나
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      많은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      의미를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      내포하고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      실제로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하루
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일과
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      속에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모습이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어떠한지는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다음과
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      같습니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
            </section>
            <section id={'music-focus-zones-audio-qualities'}>
              <h5 className={classes.subSection}>
                {translate(
                  <>6&nbsp;AM&nbsp;— 11&nbsp;AM: The Gentle Wake-Up</>,
                  <>6:00 a. m. – 11:00 a. m.: Despertar suave</>,
                  <>6 h 00 – 11 h 00 : Réveil en douceur</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      오전
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      6시
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      &nbsp;—
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      11시:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      부드러운
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기상
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </h5>
            </section>
            <section id={'what-to-learn-in-this-guide-2'}>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Your morning customers are in a fragile state. They haven’t
                    fully arrived yet. The music should feel like sunlight
                    through curtains&nbsp;— warm, present, not demanding
                    anything.
                  </>,
                  <>
                    Tus clientes de la mañana se encuentran en un estado frágil.
                    Aún no han llegado del todo. La música debería sentirse como
                    la luz del sol filtrándose a través de las cortinas: cálida,
                    presente y sin exigir nada.
                  </>,
                  <>
                    Vos clients du matin sont dans un état de fragilité. Ils ne
                    sont pas encore tout à fait arrivés. La musique devrait
                    évoquer la lumière du soleil filtrant à travers les rideaux
                    : chaleureuse, présente, sans rien exiger.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아침
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      손님들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아직
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여린
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      상태에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아직
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      온전히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이곳에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      도착하지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      못했습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      따라서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      마치
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      커튼
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사이로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비쳐
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      드는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      햇살처럼
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      느껴져야
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      합니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      따뜻하고,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      존재감이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      느껴지되,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어떤
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      요구하지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      않는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그런
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악이어야
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      합니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Think indie folk, acoustic pop, gentle electronica. Artists
                    like Novo Amor, Fleet Foxes, José González, Iron & Wine.
                    Music that feels handmade. Music with space in it.
                  </>,
                  <>
                    Piensa en indie folk, pop acústico, electrónica suave.
                    Artistas como Novo Amor, Fleet Foxes, José González, Iron &
                    Wine. Música que se siente hecha a mano. Música con espacio.
                  </>,
                  <>
                    Pensez indie folk, pop acoustique, electronica douce. Des
                    artistes comme Novo Amor, Fleet Foxes, José González, Iron &
                    Wine. Une musique qui semble faite main. Une musique qui
                    respire.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인디
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      포크,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어쿠스틱
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      팝,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      혹은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      잔잔한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일렉트로니카를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      떠올려
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보세요.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Novo
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Amor,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Fleet
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Foxes,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      José
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      González,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Iron
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      &
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Wine
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      같은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아티스트들
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      말이죠.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      마치
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      손수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      듯한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      느낌의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      안에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여백이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      깃든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    This isn’t the time to show off how eclectic your taste is.
                    It’s the time to make someone feel like they made the right
                    choice walking through your door.
                  </>,
                  <>
                    Este no es el momento de alardear de lo ecléctico que es tu
                    gusto. Es el momento de hacer que alguien sienta que tomó la
                    decisión correcta al cruzar tu puerta.
                  </>,
                  <>
                    Ce n’est pas le moment d’étaler l’éclectisme de vos goûts.
                    C’est le moment de donner à quelqu’un le sentiment d’avoir
                    fait le bon choix en franchissant votre porte.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지금은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      자신의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      취향이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      얼마나
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다채로운지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      뽐낼
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      때가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아닙니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지금은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      누군가가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      당신의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      문을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      들어선
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      올바른
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      선택이었다고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      느끼게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      줄
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      때입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
            </section>
            <section id={'examples-of-music-for-focus-zones'}>
              <h5 className={classes.subSection}>
                {translate(
                  <>11&nbsp;AM&nbsp;— 3&nbsp;PM: The Focus Zone</>,
                  <>11:00 a. m.&nbsp;— 3:00 p. m.: La zona de concentración</>,
                  <>11 h – 15 h : La zone de concentration</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      오전
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      11시
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      &nbsp;—
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      오후
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      3시:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      집중
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구역
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </h5>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Your café fills with laptops. The morning energy has
                    settled. People want to think.
                  </>,
                  <>
                    Tu cafetería se llena de portátiles. La energía matutina se
                    ha asentado. La gente quiere pensar.
                  </>,
                  <>
                    Votre café se remplit d'ordinateurs portables. L'énergie
                    matinale s'est apaisée. Les gens veulent réfléchir.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      당신의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      카페가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      노트북으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가득
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      찹니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아침의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      활기는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가라앉았습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사람들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      생각하고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      싶어
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      합니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    This is where lo-fi hip hop, ambient jazz, and instrumental
                    tracks earn their keep. Nujabes. Khruangbin. GoGo Penguin.
                    Tom Misch’s instrumental work.
                  </>,
                  <>
                    Este es el escenario preciso donde el Lo-fi Hip-hop, el
                    Ambient Jazz y los temas instrumentales brillan con luz
                    propia. Nujabes. Khruangbin. GoGo Penguin. Los temas
                    instrumentales de Tom Misch.
                  </>,
                  <>
                    C’est précisément sur cette scène que le Lo-fi Hip-hop,
                    l’Ambient Jazz et les morceaux instrumentaux brillent
                    véritablement. Nujabes. Khruangbin. GoGo Penguin. Les
                    instrumentaux de Tom Misch.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이곳이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      로파이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      힙합,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      앰비언트
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      재즈,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그리고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      연주곡들이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      진가를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      발휘하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      무대입니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Nujabes.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Khruangbin.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      GoGo
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Penguin.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Tom
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Misch의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      연주곡들.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The key insight here: lyrics compete with thought.
                    Instrumental music supports a flow state without creating
                    silence. Your solo workers stay longer. Your corner booth
                    business lunches don’t feel like they’re being eavesdropped
                    on.
                  </>,
                  <>
                    La clave aquí es la siguiente: las letras compiten con el
                    pensamiento. La música instrumental favorece un estado de
                    fluidez sin generar silencio. Tus trabajadores individuales
                    permanecen más tiempo. Tus almuerzos de negocios en los
                    reservados no dan la sensación de estar siendo escuchados.
                  </>,
                  <>
                    L'idée clé ici : les paroles entrent en concurrence avec la
                    pensée. La musique instrumentale favorise un état de
                    fluidité sans pour autant créer de silence. Vos
                    collaborateurs travaillant en solo restent plus longtemps.
                    Vos déjeuners d'affaires, installés dans un coin discret, ne
                    donnent pas l'impression d'être écoutés.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여기서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      핵심적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      통찰은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가사가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      생각과
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경쟁한다는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것입니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      연주곡은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      침묵을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만들지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      않고도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      몰입
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      상태를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지원합니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      따라서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      혼자
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      직원들이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      오래
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      머무릅니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구석
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      부스에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비즈니스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      점심
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      식사는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      엿듣는다는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      느낌이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      들지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      않습니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
            </section>
            <section id={'examples-of-music-for-focus-zones'}>
              <h5 className={classes.subSection}>
                {translate(
                  <>3&nbsp;PM&nbsp;— 6&nbsp;PM: The Social Shift</>,
                  <>3:00 p. m.&nbsp;— 6:00 p. m.: El turno social</>,
                  <>15 h – 18 h : Le virage social</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      오후
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      3시
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      &nbsp;—
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      오후
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      6시:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소셜
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시프트
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </h5>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Something changes in the mid-afternoon. Laptops start
                    closing. People start talking.
                  </>,
                  <>
                    Algo cambia a media tarde. Las computadoras portátiles
                    empiezan a cerrarse. La gente empieza a hablar.
                  </>,
                  <>
                    Quelque chose change en milieu d'après-midi. Les ordinateurs
                    portables commencent à se refermer. Les gens commencent à
                    parler.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      달라진다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      노트북
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      덮개가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      닫히기
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시작하고,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사람들이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대화를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      나누기
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시작한다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    This is when you bring the vocals back. Neo-soul, bossa
                    nova, upbeat indie, world music. Hozier. Seu Jorge. Lianne
                    La Havas. Anderson Paak.
                  </>,
                  <>
                    reintroduces las voces. Neo-soul, bossa nova, indie animado,
                    músicas del mundo. Hozier. Seu Jorge. Lianne La Havas.
                    Anderson Paak.
                  </>,
                  <>
                    C'est le moment de faire revenir le chant. Neo-soul, bossa
                    nova, indie rythmé, musiques du monde. Hozier. Seu Jorge.
                    Lianne La Havas. Anderson Paak.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이때
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보컬을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다시
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      불러옵니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      네오
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소울,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보사노바,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경쾌한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      인디
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      월드
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      뮤직.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      호지어(Hozier),
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      세우
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      조르지(Seu
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Jorge),
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      리앤
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하바스(Lianne
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      La
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Havas),
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      앤더슨
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      팩(Anderson
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Paak).
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The tempo lifts slightly. The personality of your space
                    comes forward. This is when someone looks up from their
                    conversation and asks, “Wait&nbsp;— who is this?” That
                    moment of connection is worth more than any marketing
                    campaign. It turns a customer into a regular.
                  </>,
                  <>
                    El ritmo se acelera ligeramente. La personalidad de tu
                    espacio sale a relucir. Es entonces cuando alguien levanta
                    la vista de su conversación y pregunta: «Espera... ¿quién es
                    este?». Ese momento de conexión vale más que cualquier
                    campaña de marketing. Convierte a un cliente en un habitual.
                  </>,
                  <>
                    Le rythme s’accélère légèrement. La personnalité de votre
                    espace se révèle. C’est le moment où quelqu’un lève les yeux
                    de sa conversation pour demander : «&nbsp;Attendez… c’est
                    qui, ça&nbsp;?&nbsp;» Cet instant de connexion vaut bien
                    plus que n’importe quelle campagne marketing. Il transforme
                    un client en habitué.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      분위기가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      미묘하게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고조됩니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      당신의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      공간이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지닌
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고유한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      개성이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비로소
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      전면에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      드러나는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      순간입니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이때,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      누군가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대화
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      도중
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고개를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      들어
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      묻습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      “잠깐만요—이게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      누구죠?”
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      순간의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      교감은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어떤
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      마케팅
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      캠페인보다도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      큰
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가치를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지닙니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그것은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      명의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고객을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단골로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만들어
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      줍니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
            </section>
            <section id={'examples-of-music-for-focus-zones'}>
              <h5 className={classes.subSection}>
                {translate(
                  <>6&nbsp;PM&nbsp;— Close: Intimacy Over Energy</>,
                  <>
                    6:00 p. m.&nbsp;— Cierre: Intimidad por encima de la energía
                  </>,
                  <>18 h – Fermeture : L'intimité avant l'énergie</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      오후
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      6시
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      &nbsp;—
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      마감:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      에너지보다
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      친밀함
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </h5>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The evening crowd came for something different. They’re not
                    here to work or meet&nbsp;— they’re here to stay.
                  </>,
                  <>
                    La multitud de la tarde vino en busca de algo diferente. No
                    están aquí para trabajar ni para reunirse; están aquí para
                    quedarse
                  </>,
                  <>
                    La foule du soir est venue chercher quelque chose de
                    différent. Ils ne sont pas là pour travailler ou pour se
                    réunir&nbsp;— ils sont là pour rester.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      저녁
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      무렵
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      찾아온
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사람들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      뭔가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      색다른
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      찾아왔다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일이나
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만남을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      위해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      온
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      머물기
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      위해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이곳에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      왔다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Acoustic covers, classic jazz standards, chillhop. Bonobo.
                    Bill Evans. Chet Baker. Norah Jones.
                  </>,
                  <>
                    Versiones acústicas, clásicos del jazz, chillhop. Bonobo.
                    Bill Evans. Chet Baker. Norah Jones.
                  </>,
                  <>
                    Reprises acoustiques, standards de jazz classiques,
                    chillhop. Bonobo. Bill Evans. Chet Baker. Norah Jones.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어쿠스틱
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      커버,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      클래식
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      재즈
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      스탠더드,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      칠합.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Bonobo.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Bill
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Evans.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Chet
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Baker.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Norah
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Jones.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The music pulls back. Conversations get quieter and more
                    honest. Your café becomes the kind of place people remember
                    long after the coffee is gone.
                  </>,
                  <>
                    La música baja el volumen. Las conversaciones se vuelven más
                    silenciosas y más honestas. Tu cafetería se convierte en esa
                    clase de lugar que la gente recuerda mucho después de
                    haberse terminado el café.
                  </>,
                  <>
                    La musique s'estompe. Les conversations se font plus calmes,
                    plus sincères. Votre café devient le genre d'endroit dont on
                    se souvient bien après avoir fini son café.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소리가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      잦아듭니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대화는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더욱
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      조용해지고,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      한층
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      진솔해집니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      당신의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      카페는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      커피를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      마시고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      난
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      뒤에도,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      오랫동안
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사람들의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기억
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      속에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      남는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그런
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      공간이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      됩니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
            </section>
            <section id={'music-for-collaborative-areas'}>
              <h4 className={classes.subSection}>
                {translate(
                  <>The Volume Thing (That&nbsp;Everyone Gets&nbsp;Wrong)</>,
                  <>La cosa del volumen (Eso & nbsp; todas se equivocan)</>,
                  <>
                    Cette histoire de volume (que tout le monde comprend de
                    travers)
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      볼륨에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      (모두가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      잘못
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      알고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있는)
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </h4>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Target 65–70 decibels for general background music. That’s
                    roughly the volume of a normal conversation.
                  </>,
                  <>
                    Apunte a un nivel de 65 a 70 decibelios para la música de
                    fondo general. Eso equivale, aproximadamente, al volumen de
                    una conversación normal.
                  </>,
                  <>
                    Visez 65 à 70 décibels pour une musique d'ambiance générale.
                    Cela correspond approximativement au volume d'une
                    conversation normale.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일반적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      배경
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경우
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      65~70데시벨을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      목표로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하십시오.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대략
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      평범한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대화
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소리
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      정도의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음량입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Too loud: your baristas are shouting across the counter.
                    Customers feel fatigued. Nobody stays.
                  </>,
                  <>
                    Demasiado ruidoso: tus baristas gritan a través del
                    mostrador. Los clientes se sienten fatigados. Nadie se
                    queda.
                  </>,
                  <>
                    Trop bruyant&nbsp;: vos baristas crient par-dessus le
                    comptoir. Les clients se sentent épuisés. Personne ne reste.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      너무
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시끄럽습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바리스타들이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      카운터
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      너머로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고함을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지르고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있네요.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      손님들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      피로감을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      느낍니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아무도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      머물지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      않죠.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Too quiet: every conversation feels exposed. The silence
                    becomes awkward. People leave faster than you’d expect.
                  </>,
                  <>
                    Demasiado silencioso: cada conversación se siente expuesta.
                    El silencio se vuelve incómodo. La gente se marcha más
                    rápido de lo que cabría esperar.
                  </>,
                  <>
                    Trop calme&nbsp;: chaque conversation semble exposée. Le
                    silence devient gênant. Les gens partent plus vite qu’on ne
                    s’y attendrait.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      너무
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      조용하다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대화가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      훤히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      드러나는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      듯하다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      침묵은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어색해지고,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사람들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      예상보다
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      훨씬
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      빨리
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      자리를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      뜬다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The Goldilocks zone is where people relax without realizing
                    why. That’s the goal.
                  </>,
                  <>
                    La Zona Ricitos de Oro es un lugar donde las personas se
                    sienten cómodas sin siquiera saber por qué. Ese es
                    precisamente el objetivo.
                  </>,
                  <>
                    La zone Boucles d'or est un endroit où l'on se sent à
                    l'aise, sans même savoir pourquoi. C'est précisément
                    l'objectif.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      골디락스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      존은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사람들이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      왜
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그런지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      미처
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      깨닫지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      못한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      채
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      편안함을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      느끼는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      곳입니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그것이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      목표입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    One more thing on volume: it should vary slightly with the
                    time of day. Slightly quieter in the morning. Slightly
                    fuller in the social afternoon hours. Small adjustments, big
                    atmospheric difference.
                  </>,
                  <>
                    Una cosa más sobre el volumen: debería variar ligeramente
                    según la hora del día. Ligeramente más bajo por la mañana.
                    Ligeramente más intenso durante las horas sociales de la
                    tarde. Pequeños ajustes, gran diferencia en el ambiente.
                  </>,
                  <>
                    Une dernière chose concernant le volume : celui-ci devrait
                    varier légèrement selon le moment de la journée. Un peu plus
                    bas le matin ; un peu plus ample durant les heures
                    conviviales de l'après-midi. De légers ajustements, pour une
                    différence d'ambiance considérable.
                  </>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      볼륨에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      말씀드리자면,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시간대에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      따라
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소리의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      크기를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      미세하게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      조절하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      좋습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아침
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시간대에는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      조금
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      차분하게,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사람들이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어울리는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      오후
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시간대에는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      조금
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      풍성하게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      말이죠.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아주
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      작은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      조정만으로도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      공간의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      분위기는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      확연히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      달라집니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <section id={'collaborative-areas-audio-qualities'}>
                <h4 className={classes.subSection}>
                  {translate(
                    <>Playlist Length: The Repetition Trap</>,
                    <>
                      Duración de la lista de reproducción: La trampa de la
                      repetición
                    </>,
                    <>Durée de la playlist : le piège de la répétition</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재생목록
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        길이:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        반복의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        덫
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </h4>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      If your regulars hear the same song twice in a single
                      visit, you’ve broken the spell.
                    </>,
                    <>
                      Si tus clientes habituales escuchan la misma canción dos
                      veces en una sola visita, has roto el hechizo.
                    </>,
                    <>
                      Si vos habitués entendent la même chanson deux fois au
                      cours d'une seule visite, vous avez rompu le charme.
                    </>,
                    <>
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        단골손님들이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        번의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방문
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        중에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        같은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        노래를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        두
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        번
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        듣게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        된다면,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        당신은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        마법을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        깨뜨린
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        셈입니다.
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Under 4 hours: noticeable repetition&nbsp;— kills the
                        atmosphere
                      </>,
                      <>
                        Menos de 4 horas: repetición notoria&nbsp;— arruina la
                        atmósfera.
                      </>,
                      <>
                        Moins de 4 heures&nbsp;: répétition notable&nbsp;— brise
                        l'atmosphère.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          4시간
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          미만:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          눈에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          띄는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          반복
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          &nbsp;—
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분위기를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          해친다.
                        </span>
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>4–5 hours: minimum acceptable</>,
                      <>4–5 horas: mínimo aceptable</>,
                      <>4 à 5 heures : minimum acceptable</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          4~5시간:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최소
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          허용치
                        </span>
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>6–8 hours: the sweet spot</>,
                      <>6–8 horas: el punto ideal</>,
                      <>6 à 8 heures&nbsp;: le juste milieu</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          6~8시간:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최적의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시간
                        </span>
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Rotating daily playlists: keeps even your most loyal
                        regulars engaged
                      </>,
                      <>
                        Listas de reproducción diarias rotativas: mantienen
                        involucrados incluso a tus clientes habituales más
                        leales.
                      </>,
                      <>
                        Des playlists quotidiennes renouvelées : pour maintenir
                        l'engagement, même de vos habitués les plus fidèles.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          매일 바뀌는{' '}
                        </span>
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        플레이리스트:
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        가장
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        충성도
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        높은
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        단골
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        고객까지도
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        지속적으로
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        사로잡습니다.
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                </ul>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      This is one reason the DIY approach becomes painful fast.
                      Curating 6–8 hours of genre-appropriate, legally licensed
                      music across four dayparts&nbsp;— and rotating it&nbsp;—
                      is essentially a part-time job.
                    </>,
                    <>
                      Esta es, precisamente, una de las razones por las que el
                      enfoque de «hazlo tú mismo» puede convertirse rápidamente
                      en una tarea extenuante. Seleccionar&nbsp;—y rotar
                      periódicamente— entre seis y ocho horas de música con
                      licencia legal y adecuada al género, distribuidas en
                      cuatro franjas horarias diarias distintas, equivale, en
                      realidad, a un trabajo a tiempo parcial.
                    </>,
                    <>
                      C’est précisément l’une des raisons pour lesquelles
                      l’approche «&nbsp;fait maison&nbsp;» peut rapidement se
                      transformer en une corvée épuisante. Sélectionner&nbsp;—
                      et renouveler périodiquement&nbsp;— six à huit heures de
                      musique dûment licenciée et adaptée au genre, réparties
                      sur quatre créneaux horaires distincts au quotidien,
                      équivaut en réalité à un emploi à temps partiel.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        바로 이것이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      DIY
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      방식이
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      순식간에
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      고된
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      잡무로
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      변질될
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      수
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      있는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      이유
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      중
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      하나입니다.
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      매일
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      네
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      개의
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      서로
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      다른
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      시간대에
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      맞춰,
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      법적으로
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      정식
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      라이선스를
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      획득한
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      장르
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      적합성
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      높은
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      음악을
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      6~8시간
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      분량만큼
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      선별하고
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      주기적으로
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      교체하는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      일은,
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      사실상
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      파트타임
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      업무나
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      다름없습니다.
                    </>,
                    selectedLanguage,
                  )}
                </p>
              </section>
              <section id={'collaborative-areas-design-thoughts'}>
                <h4 className={classes.subSection}>
                  {translate(
                    <>The Actual Solution: Licensed Music Services for Cafés</>,
                    <>
                      La solución real: servicios de música con licencia para
                      cafeterías.
                    </>,
                    <>
                      La véritable solution : des services de musique sous
                      licence pour les cafés
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        진정한 해결책:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      카페를
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      위한
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      라이선스
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      음악
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      서비스
                    </>,
                    selectedLanguage,
                  )}
                </h4>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Here’s the good news. You don’t need to manage ASCAP, BMI,
                      and SESAC licenses separately. You don’t need to curate
                      30+ hours of playlists manually. You don’t need a lawyer
                      on retainer.
                    </>,
                    <>
                      Aquí está la buena noticia: no necesitas gestionar por
                      separado las licencias de ASCAP, BMI y SESAC. No necesitas
                      curar manualmente más de 30 horas de listas de
                      reproducción. Y no necesitas tener un abogado de cabecera.
                    </>,
                    <>
                      Voici la bonne nouvelle : vous n’avez pas besoin de gérer
                      séparément les licences ASCAP, BMI et SESAC. Vous n’avez
                      pas besoin de constituer manuellement plus de 30 heures de
                      playlists. Vous n’avez pas besoin d’un avocat sous
                      contrat.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        여기 반가운
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      소식이
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      있습니다.
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      ASCAP,
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      BMI,
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      그리고
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      SESAC
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      라이선스를
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      일일이
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      따로
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      관리하실
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      필요가
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      없습니다.
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      30시간이
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      넘는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      분량의
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      플레이리스트를
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      직접
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      선곡하고
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      관리하실
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      필요도
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      없습니다.
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      또한,
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      상시
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      자문
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      변호사를
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      고용하실
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      필요도
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      없습니다.
                    </>,
                    selectedLanguage,
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Licensed café music services handle all of it&nbsp;—
                      performance rights, mechanical rights, curation,
                      dayparting, everything&nbsp;— for $15–50 per month.
                    </>,
                    <>
                      Los servicios de música con licencia para cafeterías se
                      encargan de todo&nbsp;— derechos de ejecución, derechos
                      mecánicos, curación, programación por franjas horarias,
                      todo&nbsp;— por entre $15 y $50 al mes.
                    </>,
                    <>
                      Les services de musique sous licence pour cafés prennent
                      tout en charge&nbsp;— droits d'exécution, droits
                      mécaniques, curation, programmation par créneaux horaires,
                      absolument tout&nbsp;— pour 15 à 50&nbsp;$ par mois.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정식 라이선스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      갖춘
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      카페
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      음악
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      서비스는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      공연권,
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      복제권,
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      큐레이션,
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      시간대별
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      선곡
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      등
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      이
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      모든
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      것을
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      월
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      15~50달러에
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      해결해
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      드립니다.
                    </>,
                    selectedLanguage,
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>What to look for in a service:</>,
                    <>Qué buscar en un servicio:</>,
                    <>
                      Ce qu'il faut rechercher chez un prestataire de services :
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        서비스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선택할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        때
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        살펴봐야
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        점:
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        All performance and mechanical rights included (ask for
                        documentation)
                      </>,
                      <>
                        Todos los derechos de ejecución y mecánicos incluidos
                        (solicite la documentación).
                      </>,
                      <>
                        Tous les droits d'exécution publique et mécaniques sont
                        inclus (demander la documentation).
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공연권
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기계적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          복제권
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          포함
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (관련
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          서류
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요청
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가능)
                        </span>
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Dayparting capability&nbsp;— automatic transitions by
                        time of day
                      </>,
                      <>
                        Capacidad de segmentación horaria&nbsp;— transiciones
                        automáticas según la hora del día
                      </>,
                      <>
                        Fonctionnalité de segmentation horaire&nbsp;—
                        transitions automatiques selon l'heure de la journée
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시간대별 운영
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        기능
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        &nbsp;—
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        시간대에
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        따른
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        자동
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        전환
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Genre controls&nbsp;— ability to exclude or include
                        specific artists
                      </>,
                      <>
                        Controles de género&nbsp;— capacidad para excluir o
                        incluir artistas específicos
                      </>,
                      <>
                        Contrôles par genre&nbsp;— possibilité d'exclure ou
                        d'inclure des artistes spécifiques
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장르
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제어
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          &nbsp;—
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          특정
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아티스트를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제외하거나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          포함하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기능
                        </span>
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Human curation, not just algorithms</>,
                      <>Curación humana, no solo algoritmos</>,
                      <>
                        Une curation humaine, et non pas seulement des
                        algorithmes
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          단순한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          알고리즘을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          넘어선,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인간의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          큐레이션
                        </span>
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        99%+ uptime (dead air during your morning rush is its
                        own crisis)
                      </>,
                      <>
                        Más del 99 % de tiempo de actividad (el silencio total
                        durante la hora punta matutina constituye, por sí mismo,
                        una crisis).
                      </>,
                      <>
                        Plus de 99 % de disponibilité (un silence radio durant
                        votre coup de feu matinal constitue une crise à part
                        entière).
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          99% 이상의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가동률
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        (아침
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        출근길
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        피크
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        타임에
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        방송이
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        끊기는
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        것은
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        그
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        자체로
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        재앙입니다)
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                </ul>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Services worth evaluating:{' '}
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={translate(
                          MAIN_SITE_ENVIRONMENT_HOST,
                          MAIN_SITE_ENVIRONMENT_HOST_ES,
                          MAIN_SITE_ENVIRONMENT_HOST_FR,
                          MAIN_SITE_ENVIRONMENT_HOST_KO,
                          selectedLanguage,
                        )}
                        title={translate(
                          'The least expensive, full-featured service for licensed commercial background music.',
                          'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                          "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                          '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                          selectedLanguage,
                        )}
                        rel="noopener noreferrer"
                      >
                        Practical Stream
                      </a>
                      , Cloud Cover Music, Soundtrack Your Brand, Rockbot. Most
                      offer free trials. Test a few during actual business hours
                      &nbsp;— what sounds good at home doesn’t always translate
                      to a room full of espresso machines and conversation.
                    </>,
                    <>
                      Servicios que vale la pena explorar:{' '}
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={translate(
                          MAIN_SITE_ENVIRONMENT_HOST,
                          MAIN_SITE_ENVIRONMENT_HOST_ES,
                          MAIN_SITE_ENVIRONMENT_HOST_FR,
                          MAIN_SITE_ENVIRONMENT_HOST_KO,
                          selectedLanguage,
                        )}
                        title={translate(
                          'The least expensive, full-featured service for licensed commercial background music.',
                          'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                          "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                          '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                          selectedLanguage,
                        )}
                        rel="noopener noreferrer"
                      >
                        Practical Stream
                      </a>
                      , Cloud Cover Music, Soundtrack Your Brand y Rockbot. La
                      mayoría ofrece pruebas gratuitas. Prueba algunos durante
                      el horario comercial real; lo que suena bien en casa no
                      siempre se traduce igual en una sala repleta de máquinas
                      de espresso y conversaciones.
                    </>,
                    <>
                      Des services qui méritent d'être explorés&nbsp;:{' '}
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={translate(
                          MAIN_SITE_ENVIRONMENT_HOST,
                          MAIN_SITE_ENVIRONMENT_HOST_ES,
                          MAIN_SITE_ENVIRONMENT_HOST_FR,
                          MAIN_SITE_ENVIRONMENT_HOST_KO,
                          selectedLanguage,
                        )}
                        title={translate(
                          'The least expensive, full-featured service for licensed commercial background music.',
                          'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                          "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                          '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                          selectedLanguage,
                        )}
                        rel="noopener noreferrer"
                      >
                        Practical Stream
                      </a>
                      , Cloud Cover Music, Soundtrack Your Brand et Rockbot. La
                      plupart proposent des essais gratuits. Testez-en
                      quelques-uns durant vos heures d'ouverture effectives ; ce
                      qui sonne bien à la maison ne rend pas toujours de la même
                      manière dans une pièce remplie de machines à expresso et
                      de conversations.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        검토해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        볼
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        만한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스로는
                      </span>{' '}
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={translate(
                          MAIN_SITE_ENVIRONMENT_HOST,
                          MAIN_SITE_ENVIRONMENT_HOST_ES,
                          MAIN_SITE_ENVIRONMENT_HOST_FR,
                          MAIN_SITE_ENVIRONMENT_HOST_KO,
                          selectedLanguage,
                        )}
                        title={translate(
                          'The least expensive, full-featured service for licensed commercial background music.',
                          'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                          "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                          '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                          selectedLanguage,
                        )}
                        rel="noopener noreferrer"
                      >
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Practical
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Stream
                        </span>
                      </a>
                      ,{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Cloud
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Cover
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Music,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Soundtrack
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Your
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Brand,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Rockbot이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        대부분
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        무료
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        체험
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        제공합니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실제
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        영업
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        시간
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        중에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        몇
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        직접
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        테스트해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        보십시오.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        집에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        들었을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        때는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        좋게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        느껴지던
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악이라도,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        에스프레소
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        머신
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소음과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        손님들의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        대화
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소리로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가득
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        찬
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        매장
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        공간에서는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        그
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        느낌이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        그대로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전달되지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        않을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있기
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        때문입니다.
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The math is not complicated. Thirty dollars a month versus
                      a single copyright settlement that starts at $2,000 and
                      can reach $150,000. There is no version of this where
                      DIY-ing it with a personal streaming account makes
                      financial sense.
                    </>,
                    <>
                      Las cuentas no son complicadas. Treinta dólares al mes
                      frente a un único acuerdo por derechos de autor que
                      arranca en 2.000 dólares y puede alcanzar los 150.000. No
                      existe escenario alguno en el que intentar gestionarlo por
                      cuenta propia&nbsp;—utilizando una cuenta de *streaming*
                      personal— tenga sentido desde el punto de vista
                      financiero.
                    </>,
                    <>
                      Le calcul n’a rien de compliqué. Trente dollars par mois,
                      contre un règlement unique pour violation de droits
                      d’auteur qui débute à 2&nbsp;000&nbsp;$ et peut atteindre
                      150&nbsp;000&nbsp;$. Quelle que soit la perspective, gérer
                      cela soi-même à l’aide d’un compte de streaming personnel
                      n’a aucun sens sur le plan financier.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        계산은 복잡하지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      않습니다.
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      월
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      30달러를
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      지불하는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      것과,
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      최소
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      2,000달러에서
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      최대
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      15만
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      달러까지
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      치솟을
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      수
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      있는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      단
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      한
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      번의
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      저작권
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      합의금을
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      내는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      것
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      중
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      무엇이
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      더
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      나을까요?
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      개인용
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      스트리밍
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      계정을
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      이용해
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      직접
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      해결하려
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      드는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      것이
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      금전적으로
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      합리적인
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      선택이
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      되는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      경우는
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      그
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      어떤
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      경우에도
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      존재하지
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      ></span>{' '}
                      않습니다.
                    </>,
                    selectedLanguage,
                  )}
                </p>
              </section>
              <section id={'collaborative-areas-music-examples'}>
                <h4 className={classes.subSection}>
                  {translate(
                    <>Getting This Right: A Simple 4-Week Setup</>,
                    <>Hacerlo bien: Una configuración sencilla de 4 semanas</>,
                    <>Réussir&nbsp;: une mise en place simple sur 4 semaines</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제대로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        해내기:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        4주간의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        간단한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        준비
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        과정
                      </span>
                    </>,
                    selectedLanguage,
                  )}
                </h4>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Week 1&nbsp;— Audit and choose. Stop using your personal
                        streaming account immediately. Sign up for trials of 2–3
                        licensed services. Test them during real business hours,
                        not at your kitchen table.
                      </>,
                      <>
                        Semana 1&nbsp;— Audita y elige. Deja de utilizar tu
                        cuenta personal de *streaming* de inmediato. Regístrate
                        en las pruebas de 2 o 3 servicios con licencia.
                        Pruébalos durante el horario laboral real, no en la mesa
                        de tu cocina.
                      </>,
                      <>
                        Semaine 1&nbsp;— Auditer et choisir. Cessez
                        immédiatement d'utiliser votre compte de streaming
                        personnel. Inscrivez-vous à des essais gratuits de 2 ou
                        3 services sous licence. Testez-les durant les heures de
                        travail effectives, et non à votre table de cuisine.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          1주 차
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        &nbsp;—
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        점검
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        및
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        선택.
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        개인용
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        스트리밍
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        계정
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        사용을
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        즉시
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        중단하십시오.
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        정식
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        라이선스를
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        보유한
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        서비스
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        2~3곳의
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        체험판에
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        가입하세요.
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        테스트는
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        집
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        주방
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        식탁에서가
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        아닌,
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        실제
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        업무
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        시간
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        중에
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        진행하십시오.
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Week 2&nbsp;— Build your strategy. Map out your daypart
                        schedule. Work with the service to select or build
                        playlists for each block. Train your staff on volume
                        levels (write it down&nbsp;— “65–70 dB, conversations
                        comfortable” is enough).
                      </>,
                      <>
                        Semana 2&nbsp;— Elabore su estrategia. Diseñe su
                        programación por franjas horarias. Colabore con el
                        servicio para seleccionar o crear listas de reproducción
                        para cada bloque. Capacite a su personal sobre los
                        niveles de volumen (póngalo por escrito: «65–70 dB,
                        conversaciones cómodas» es suficiente).
                      </>,
                      <>
                        Semaine 2&nbsp;— Élaborez votre stratégie. Planifiez
                        votre découpage horaire. Collaborez avec le service pour
                        sélectionner ou créer des playlists pour chaque créneau.
                        Formez votre personnel aux niveaux sonores (mettez-le
                        par écrit : «&nbsp;65–70 dB, conversations
                        confortables&nbsp;» suffit).
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          2주 차
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        &nbsp;—
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        전략을
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        수립하세요.
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        시간대별(Daypart)
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        스케줄을
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        구체적으로
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        계획하십시오.
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        서비스
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        제공
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        업체와
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        협력하여
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        각
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        시간대
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        블록에
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        맞는
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        플레이리스트를
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        선정하거나
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        직접
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        구성하세요.
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        직원들에게
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        적정
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        볼륨
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        레벨에
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        대해
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        교육하십시오(내용을
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        문서화해
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        두세요.
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        “65~70dB,
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        대화에
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        지장
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        없는
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        수준”
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        정도면
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        충분합니다).
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Week 3&nbsp;— Launch and listen. Casually observe. Do
                        customers seem more relaxed? Are people staying longer?
                        Are your baristas not having to shout? These are your
                        early signals.
                      </>,
                      <>
                        Semana 3&nbsp;— Lanza y escucha. Observa con
                        naturalidad. ¿Se ven los clientes más relajados? ¿Se
                        queda la gente más tiempo? ¿Ya no tienen que gritar tus
                        baristas? Estas son tus primeras señales.
                      </>,
                      <>
                        Semaine&nbsp;3&nbsp;— Lancez-vous et soyez à l'écoute.
                        Observez sans forcer. Les clients semblent-ils plus
                        détendus&nbsp;? Les gens restent-ils plus
                        longtemps&nbsp;? Vos baristas n'ont-ils plus besoin de
                        crier&nbsp;? Ce sont là vos premiers signaux.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          3주 차
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        &nbsp;—
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        런칭하고
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        귀
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        기울이세요.
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        편안한
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        마음으로
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지켜보십시오.
                        </span>{' '}
                        고객들이
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        한결
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        편안해
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        보이나요?
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        사람들이
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        매장에
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        더
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        오래
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        머무르나요?
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        바리스타들이
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        굳이
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        소리치지
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        않아도
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        되나요?
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        이것들이
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        바로
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        초기
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        신호입니다.
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Week 4&nbsp;— Optimize. Adjust the transitions. Refresh
                        anything that sounds stale. Document what’s working.
                        Then stop thinking about it&nbsp;— that’s the point.
                      </>,
                      <>
                        Semana 4&nbsp;— Optimiza. Ajusta las transiciones.
                        Renueva todo aquello que suene anticuado. Documenta lo
                        que funciona. Luego, deja de pensar en ello: de eso se
                        trata.
                      </>,
                      <>
                        Semaine 4&nbsp;— Optimisez. Ajustez les transitions.
                        Renouvelez tout ce qui semble éculé. Documentez ce qui
                        fonctionne. Puis, cessez d’y penser&nbsp;— c’est là tout
                        l’intérêt.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          4주
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          차
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          &nbsp;—
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최적화.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전환
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부분을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조정하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          진부하게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          들리는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부분은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          새롭게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다듬으세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          효과가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기록해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          두세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그리고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          나서는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          더
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이상
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          생각하지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          마세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          바로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그것이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          핵심이니까요.
                        </span>
                      </>,
                      selectedLanguage,
                    )}
                  </li>
                </ul>
              </section>
            </section>
            <section id={'break-room-music-design'}>
              <h4 className={classes.subSection}>
                {translate(
                  <>The Bigger Picture</>,
                  <>El panorama general</>,
                  <>La vue d'ensemble</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더 큰 그림
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </h4>
              <p className={classes.pParagraph}>
                {translate(
                  <>Good coffee will get someone through your door.</>,
                  <>Un buen café hará que alguien entre por tu puerta.</>,
                  <>Un bon café fera franchir votre porte à quelqu'un.</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      맛있는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      커피는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      손님을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      당신의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가게로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      불러들일
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The right atmosphere&nbsp;— the light, the smell, the feel
                    of the chairs, and yes, the music&nbsp;— is what makes them
                    come back. It’s what makes them bring their colleagues,
                    their dates, their out-of-town friends. It’s what makes your
                    café theirs.
                  </>,
                  <>
                    El ambiente adecuado&nbsp;—la luz, el aroma, la sensación de
                    las sillas y, sí, la música— es lo que hace que regresen. Es
                    lo que hace que traigan a sus colegas, a sus citas, a sus
                    amigos de fuera de la ciudad. Es lo que hace que tu café sea
                    suyo.
                  </>,
                  <>
                    La bonne atmosphère&nbsp;— la lumière, les odeurs, le
                    confort des chaises et, oui, la musique&nbsp;— voilà ce qui
                    les fait revenir. C’est ce qui les incite à amener leurs
                    collègues, leurs rendez-vous galants, leurs amis de passage.
                    C’est ce qui fait de votre café le leur.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적절한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      분위기,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      즉
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      조명,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      향기,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      편안한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      의자,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그리고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      물론
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악까지,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이런
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것들이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      손님들을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다시
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      찾아오게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만드는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      요소입니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이러한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      분위기
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      덕분에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      손님들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      동료,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      데이트
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      상대,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그리고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방문한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      친구들을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      데려오게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      됩니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이러한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      요소들이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      당신의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      카페를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그들만의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      공간으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만들어주는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Music is the one element of that atmosphere you can control
                    completely, legally, and for less than the cost of a weekly
                    bag of single-origin beans.
                  </>,
                  <>
                    La música es el único elemento de esa atmósfera que puedes
                    controlar por completo, legalmente y por menos de lo que
                    cuesta una bolsa semanal de granos de origen único.
                  </>,
                  <>
                    La musique est le seul élément de cette atmosphère que vous
                    pouvez contrôler entièrement, légalement et pour moins que
                    le prix d'un sachet hebdomadaire de grains de café d'origine
                    unique.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      분위기를—완벽하게,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      합법적으로,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그리고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일주일치
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      싱글
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      오리진
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      원두
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      봉지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      값보다도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      저렴하게—온전히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      통제할
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      유일한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      요소입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The woman with the letter? She switched to a licensed
                    service. Settled with the PRO. Got it sorted. Her café is
                    still open, still playing beautiful music, still converting
                    first-timers into regulars.
                  </>,
                  <>
                    ¿La mujer de la carta? Se cambió a un servicio con licencia.
                    Llegó a un acuerdo con la PRO. Lo dejó todo resuelto. Su
                    cafetería sigue abierta, sigue poniendo música hermosa y
                    sigue convirtiendo a los clientes primerizos en habituales.
                  </>,
                  <>
                    La femme avec la lettre&nbsp;? Elle est passée à un service
                    sous licence. Elle a trouvé un arrangement avec l'organisme
                    de gestion des droits. Tout est rentré dans l'ordre. Son
                    café est toujours ouvert, on y joue toujours de la belle
                    musique, et il continue de transformer les nouveaux venus en
                    habitués.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      편지를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      받았던
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여성
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      말인가요?
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그녀는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      정식
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      허가를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      받은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      전환했습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      저작권
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관리
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단체(PRO)와
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      합의를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      마치고,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      문제를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      깔끔하게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      해결했죠.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그녀의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      카페는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여전히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      문을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      열고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있으며,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      변함없이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      아름다운
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      들려주고,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      처음
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방문한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      손님들을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단골로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만들고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>She just wishes someone had told her sooner.</>,
                  <>
                    Ella solo desearía que alguien se lo hubiera dicho antes.
                  </>,
                  <>
                    Elle souhaiterait simplement que quelqu'un le lui ait dit
                    plus tôt.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그녀는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      누군가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      좀
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일찍
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      말해줬으면
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      좋았을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      거라고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      생각할
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      뿐이에요.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Now someone has. Running a café or retail space and want to
                    explore licensed music options?{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={translate(
                        MAIN_SITE_ENVIRONMENT_HOST,
                        MAIN_SITE_ENVIRONMENT_HOST_ES,
                        MAIN_SITE_ENVIRONMENT_HOST_FR,
                        MAIN_SITE_ENVIRONMENT_HOST_KO,
                        selectedLanguage,
                      )}
                      title={translate(
                        'The least expensive, full-featured service for licensed commercial background music.',
                        'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                        "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                        '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                        selectedLanguage,
                      )}
                      rel="noopener noreferrer"
                    >
                      Practical Stream
                    </a>{' '}
                    is worth a look&nbsp;— built specifically for small and
                    independent businesses that want professional sound without
                    enterprise pricing.
                  </>,
                  <>
                    Pues ahora alguien lo ha hecho. ¿Gestionas una cafetería o
                    un local comercial y deseas explorar opciones de música con
                    licencia? Vale la pena echar un vistazo a{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={translate(
                        MAIN_SITE_ENVIRONMENT_HOST,
                        MAIN_SITE_ENVIRONMENT_HOST_ES,
                        MAIN_SITE_ENVIRONMENT_HOST_FR,
                        MAIN_SITE_ENVIRONMENT_HOST_KO,
                        selectedLanguage,
                      )}
                      title={translate(
                        'The least expensive, full-featured service for licensed commercial background music.',
                        'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                        "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                        '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                        selectedLanguage,
                      )}
                      rel="noopener noreferrer"
                    >
                      Practical Stream
                    </a>
                    : una solución diseñada específicamente para pequeñas
                    empresas y negocios independientes que buscan un sonido
                    profesional sin los precios de las grandes corporaciones.
                  </>,
                  <>
                    C'est désormais chose faite. Vous gérez un café ou un espace
                    commercial et souhaitez explorer les options de musique sous
                    licence&nbsp;?{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={translate(
                        MAIN_SITE_ENVIRONMENT_HOST,
                        MAIN_SITE_ENVIRONMENT_HOST_ES,
                        MAIN_SITE_ENVIRONMENT_HOST_FR,
                        MAIN_SITE_ENVIRONMENT_HOST_KO,
                        selectedLanguage,
                      )}
                      title={translate(
                        'The least expensive, full-featured service for licensed commercial background music.',
                        'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                        "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                        '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                        selectedLanguage,
                      )}
                      rel="noopener noreferrer"
                    >
                      Practical Stream
                    </a>
                    &nbsp; : mérite le détour : conçu spécifiquement pour les
                    petites entreprises et les indépendants, il offre une
                    sonorisation professionnelle sans les tarifs réservés aux
                    grands groupes.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이제
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      누군가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      해냈습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      카페나
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      매장을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      운영하며
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      활용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방안을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모색
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      중이신가요?
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그렇다면
                    </span>{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={translate(
                        MAIN_SITE_ENVIRONMENT_HOST,
                        MAIN_SITE_ENVIRONMENT_HOST_ES,
                        MAIN_SITE_ENVIRONMENT_HOST_FR,
                        MAIN_SITE_ENVIRONMENT_HOST_KO,
                        selectedLanguage,
                      )}
                      title={translate(
                        'The least expensive, full-featured service for licensed commercial background music.',
                        'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                        "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                        '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                        selectedLanguage,
                      )}
                      rel="noopener noreferrer"
                    >
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Practical
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Stream을
                      </span>
                    </a>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      주목해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보십시오.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대기업
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수준의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      부담
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      없이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      전문적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사운드를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      원하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소규모
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      및
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      독립
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사업자들을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      위해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      특별히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      설계된
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스입니다.
                    </span>
                  </>,
                  selectedLanguage,
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisorBottomOfArticle} />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
            <br />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <ReadyToTransform
              selectedLanguage={selectedLanguage}
              linkVariant={false}
            />
          </Grid.Col>
          <Grid.Col md={24}>
            <div>
              <br />
              <br />
              <br />
            </div>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  )
}
